/**
 * ESPACE ADMINISTRATEUR — page d'accueil des administrateurs non propriétaires.
 * Contenu (message, tâches, pages accessibles) entièrement contrôlé par le CEO depuis /app/administrateurs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ShieldCheck, CheckSquare, Square, ArrowRight, CircleNotch, Lock } from "@phosphor-icons/react";
import { NAV_GROUPS } from "./DashboardLayout";

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.label })));

export default function EspaceAdminPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);

  const load = () => api.get("/auth/admin-space").then((r) => setCfg(r.data)).catch(() => toast.error("Impossible de charger l'espace."));
  useEffect(() => { load(); }, []);

  const toggle = async (tid) => {
    try {
      await api.post(`/auth/admin-space/tasks/${tid}/toggle`);
      setCfg((c) => ({ ...c, tasks: c.tasks.map((t) => (t.id === tid ? { ...t, done: !t.done } : t)) }));
    } catch { toast.error("Erreur"); }
  };

  if (!cfg) return <div className="p-12 text-zinc-400 flex items-center gap-2"><CircleNotch className="animate-spin" /> Chargement…</div>;
  const pages = (cfg.allowed_paths || []).map((p) => ALL_ITEMS.find((it) => it.to === p)).filter(Boolean);
  const doneCount = (cfg.tasks || []).filter((t) => t.done).length;

  return (
    <div className="px-8 lg:px-12 py-10 max-w-5xl" data-testid="admin-space-page">
      <div className="flex items-start gap-4 mb-8">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
          <ShieldCheck size={30} weight="duotone" className="text-amber-400" />
        </div>
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-amber-400/80 font-bold">Accès administrateur</div>
          <h1 className="text-3xl md:text-4xl font-black text-zinc-100 font-display" data-testid="admin-space-title">{cfg.title}</h1>
          <p className="text-zinc-400 mt-1 text-sm">Bonjour {user?.full_name || user?.email} 👋</p>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 mb-8 whitespace-pre-wrap text-zinc-200 leading-relaxed" data-testid="admin-space-message">
        {cfg.welcome_message}
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <section className="md:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base md:text-lg font-bold text-zinc-100">Tes pages</h2>
            <span className="text-xs text-zinc-500">{pages.length} page(s) ouverte(s) par le CEO</span>
          </div>
          {pages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-zinc-500 text-sm" data-testid="admin-space-no-pages">
              <Lock size={28} className="mx-auto mb-2 opacity-50" />
              Aucune page n'a encore été ouverte pour toi. Le CEO peut en ajouter depuis « Accès & rôles ».
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-space-pages">
              {pages.map((it) => {
                const Icon = it.icon;
                return (
                  <Link key={it.to} to={it.to} data-testid={`admin-space-page-${it.to.replace(/\//g, "-")}`}
                    className="group rounded-xl border border-zinc-800 bg-zinc-950/60 hover:border-amber-500/50 hover:bg-zinc-900 p-4 flex items-center gap-3 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-zinc-900 group-hover:bg-amber-500/15 flex items-center justify-center shrink-0 transition-colors">
                      {Icon ? <Icon size={20} weight="duotone" className="text-amber-400" /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-zinc-100 truncate">{it.label}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{it.group}</div>
                    </div>
                    <ArrowRight size={16} className="text-zinc-600 group-hover:text-amber-400 transition-colors" />
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="md:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base md:text-lg font-bold text-zinc-100">À faire</h2>
            {(cfg.tasks || []).length > 0 && <span className="text-xs text-zinc-500">{doneCount}/{cfg.tasks.length}</span>}
          </div>
          {(cfg.tasks || []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-800 p-6 text-center text-zinc-500 text-sm" data-testid="admin-space-no-tasks">Aucune tâche pour l'instant.</div>
          ) : (
            <ul className="space-y-2" data-testid="admin-space-tasks">
              {cfg.tasks.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => toggle(t.id)} data-testid={`admin-space-task-${t.id}`}
                    className={`w-full text-left flex items-start gap-2.5 rounded-lg border p-3 text-sm transition-colors ${t.done ? "border-emerald-500/30 bg-emerald-500/5 text-zinc-500 line-through" : "border-zinc-800 bg-zinc-950/60 text-zinc-200 hover:border-zinc-600"}`}>
                    {t.done ? <CheckSquare size={18} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> : <Square size={18} className="text-zinc-500 shrink-0 mt-0.5" />}
                    <span>{t.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
